class AuthCloudBackground extends React.Component {
  componentDidMount() {
    this.canvas = this.canvasRef;
    this.ctx = this.canvas.getContext('2d');
    this.pointer = { x: 0.5, y: 0.46, tx: 0.5, ty: 0.46 };
    this.seed = 4.219;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.motionQuery.matches;

    this.resize();
    this.bindEvents();
    this.start();
  }

  componentWillUnmount() {
    this.unbindEvents();
    this.stop();
  }

  bindEvents() {
    window.addEventListener('resize', this.resize, { passive: true });
    window.addEventListener('pointermove', this.trackPointer, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.motionQuery.addEventListener)
      this.motionQuery.addEventListener('change', this.handleMotionPreference);
    else
      this.motionQuery.addListener(this.handleMotionPreference);
  }

  unbindEvents() {
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('pointermove', this.trackPointer);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (!this.motionQuery)
      return;

    if (this.motionQuery.removeEventListener)
      this.motionQuery.removeEventListener('change', this.handleMotionPreference);
    else
      this.motionQuery.removeListener(this.handleMotionPreference);
  }

  start() {
    this.stop();

    if (this.reducedMotion) {
      this.draw(0);
      return;
    }

    this.frame = window.requestAnimationFrame(this.tick);
  }

  stop() {
    if (!this.frame)
      return;

    window.cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  resize = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 1.6);
    this.width = width;
    this.height = height;
    this.canvas.width = Math.round(width * this.pixelRatio);
    this.canvas.height = Math.round(height * this.pixelRatio);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.draw(0);
  }

  trackPointer = (event) => {
    this.pointer.tx = event.clientX / Math.max(this.width, 1);
    this.pointer.ty = event.clientY / Math.max(this.height, 1);
  }

  handleVisibilityChange = () => {
    if (document.hidden)
      this.stop();
    else
      this.start();
  }

  handleMotionPreference = (event) => {
    this.reducedMotion = event.matches;
    this.start();
  }

  tick = (time) => {
    this.draw(time * 0.001);
    this.frame = window.requestAnimationFrame(this.tick);
  }

  draw(time) {
    const ctx = this.ctx;

    if (!ctx)
      return;

    ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);

    this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.035;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.035;
    this.flow = this.automaticFlow(time);

    this.drawAtmosphere(ctx, time);
    this.drawCloudMasses(ctx, time);
    this.drawCloudSheets(ctx, time);
    this.drawFlowCurrent(ctx, time);
    this.drawSignalContours(ctx, time);
    this.drawCloudNodes(ctx, time);
    this.drawSoftHighlights(ctx, time);
    this.drawVignette(ctx);
  }

  automaticFlow(time) {
    return {
      x: Math.sin(time * 0.14) * 72 + Math.sin(time * 0.043 + 1.4) * 36,
      y: Math.cos(time * 0.105 + 0.7) * 38 + Math.sin(time * 0.058) * 24,
      stream: time * 38,
      phase: time * 0.42
    };
  }

  drawAtmosphere(ctx, time) {
    const gradient = ctx.createLinearGradient(0, 0, this.width, this.height);
    gradient.addColorStop(0, '#eefaff');
    gradient.addColorStop(0.38, '#f8fcff');
    gradient.addColorStop(0.68, '#eaf8f4');
    gradient.addColorStop(1, '#f7fbfd');

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 0.74;
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    const sweep = ctx.createLinearGradient(this.width * 0.18, 0, this.width * 0.9, this.height);
    const drift = Math.sin(time * 0.045) * 0.04;
    sweep.addColorStop(Math.max(0, 0.05 + drift), 'rgba(81, 191, 213, 0)');
    sweep.addColorStop(0.42 + drift, 'rgba(81, 191, 213, 0.12)');
    sweep.addColorStop(0.66 + drift, 'rgba(255, 255, 255, 0.22)');
    sweep.addColorStop(1, 'rgba(232, 246, 249, 0)');

    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = sweep;
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.globalAlpha = 1;
  }

  drawCloudMasses(ctx, time) {
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.filter = 'blur(24px)';

    const masses = [
      { x: 0.08, y: 0.2, radius: 300, color: '255, 255, 255', alpha: 0.55, speed: 0.018 },
      { x: 0.72, y: 0.18, radius: 250, color: '218, 245, 250', alpha: 0.38, speed: -0.014 },
      { x: 0.23, y: 0.72, radius: 360, color: '255, 255, 255', alpha: 0.48, speed: -0.012 },
      { x: 0.86, y: 0.76, radius: 320, color: '212, 241, 231', alpha: 0.31, speed: 0.016 },
      { x: 0.5, y: 0.48, radius: 420, color: '240, 251, 254', alpha: 0.42, speed: 0.01 }
    ];

    masses.forEach((mass, index) => {
      const flowWeight = 0.28 + index * 0.08;
      const stream = ((this.flow.stream * (0.14 + index * 0.025) + this.width * (index * 0.19)) % (this.width * 0.52)) - this.width * 0.26;
      const driftX = stream + Math.sin(time * mass.speed + index) * 34 + (this.pointer.x - 0.5) * 24 + this.flow.x * flowWeight;
      const driftY = Math.cos(time * mass.speed * 1.2 + index) * 24 + (this.pointer.y - 0.5) * 16 + this.flow.y * flowWeight + stream * 0.05;
      const x = this.width * mass.x + driftX;
      const y = this.height * mass.y + driftY;
      const radius = Math.max(mass.radius, Math.min(this.width, this.height) * 0.22);
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

      gradient.addColorStop(0, `rgba(${mass.color}, ${mass.alpha})`);
      gradient.addColorStop(0.46, `rgba(${mass.color}, ${mass.alpha * 0.52})`);
      gradient.addColorStop(1, `rgba(${mass.color}, 0)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    });

    ctx.restore();
  }

  drawCloudSheets(ctx, time) {
    const sheets = [
      { y: 0.16, width: 220, alpha: 0.56, speed: 0.028, amp: 34, phase: 0.1, color: '255, 255, 255' },
      { y: 0.34, width: 260, alpha: 0.34, speed: -0.024, amp: 48, phase: 1.7, color: '218, 245, 250' },
      { y: 0.57, width: 310, alpha: 0.39, speed: 0.02, amp: 56, phase: 2.8, color: '255, 255, 255' },
      { y: 0.8, width: 270, alpha: 0.31, speed: -0.016, amp: 44, phase: 4.4, color: '212, 241, 231' }
    ];

    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.filter = 'blur(18px)';

    sheets.forEach((sheet, index) => {
      const pointerLift = (this.pointer.y - 0.5) * 42 * (index % 2 ? -1 : 1);
      const flowWeight = (index - 1.5) * 0.26;
      const stream = ((this.flow.stream * (0.34 + index * 0.06) + this.width * (index * 0.21)) % (this.width * 0.64)) - this.width * 0.32;
      const baseY = this.height * sheet.y + pointerLift + this.flow.y * flowWeight;

      ctx.beginPath();
      for (let step = 0; step <= 16; step += 1) {
        const x = -this.width * 0.16 + (this.width * 1.32 * step) / 16 + this.flow.x * flowWeight + stream;
        const y = baseY +
          Math.sin(step * 0.78 + time * sheet.speed + sheet.phase + this.flow.phase) * sheet.amp +
          Math.sin(step * 1.41 - time * sheet.speed * 0.7 + this.seed - this.flow.phase * 0.6) * sheet.amp * 0.36;

        if (step === 0)
          ctx.moveTo(x, y);
        else
          ctx.lineTo(x, y);
      }

      ctx.strokeStyle = `rgba(${sheet.color}, ${sheet.alpha})`;
      ctx.lineWidth = sheet.width;
      ctx.stroke();
    });

    ctx.restore();
  }

  drawFlowCurrent(ctx, time) {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const drawCurrent = (current) => {
      const stroke = ctx.createLinearGradient(0, 0, this.width, 0);
      stroke.addColorStop(0, `rgba(${current.color}, 0)`);
      stroke.addColorStop(0.24, `rgba(${current.color}, ${current.alpha})`);
      stroke.addColorStop(0.55, `rgba(${current.color}, ${current.alpha * 1.55})`);
      stroke.addColorStop(0.86, `rgba(${current.color}, ${current.alpha})`);
      stroke.addColorStop(1, `rgba(${current.color}, 0)`);

      ctx.beginPath();
      for (let step = 0; step <= 20; step += 1) {
        const x = -this.width * 0.12 + (this.width * 1.24 * step) / 20 + this.flow.x * (current.flowScale || 0.18);
        const y = this.height * current.y +
          Math.sin(step * 0.62 + time * current.speed + current.phase) * current.amp +
          Math.sin(step * 1.26 - time * current.speed * 0.8) * current.detailAmp +
          this.flow.y * (current.flowYScale || 0.16);

        if (step === 0)
          ctx.moveTo(x, y);
        else
          ctx.lineTo(x, y);
      }

      ctx.strokeStyle = stroke;
      ctx.lineWidth = current.width;
      ctx.stroke();
    };

    ctx.globalCompositeOperation = 'multiply';
    ctx.filter = 'blur(18px)';

    const shadowCurrents = [
      { y: 0.31, width: 142, alpha: 0.065, color: '45, 91, 108', phase: 0.4, speed: 0.18, amp: 50, detailAmp: 20, flowScale: 0.22, flowYScale: 0.18 },
      { y: 0.53, width: 124, alpha: 0.052, color: '38, 76, 96', phase: 2.2, speed: -0.15, amp: 42, detailAmp: 16, flowScale: 0.18, flowYScale: 0.14 },
      { y: 0.71, width: 136, alpha: 0.056, color: '43, 89, 92', phase: 3.8, speed: 0.13, amp: 46, detailAmp: 18, flowScale: 0.2, flowYScale: 0.16 }
    ];

    shadowCurrents.forEach(drawCurrent);

    ctx.globalCompositeOperation = 'source-over';
    ctx.filter = 'blur(8px)';

    const currents = [
      { y: 0.33, width: 70, alpha: 0.13, color: '68, 164, 196', phase: 0, speed: 0.22, amp: 46, detailAmp: 18 },
      { y: 0.5, width: 58, alpha: 0.1, color: '255, 255, 255', phase: 1.9, speed: -0.18, amp: 46, detailAmp: 18 },
      { y: 0.67, width: 64, alpha: 0.1, color: '83, 178, 145', phase: 3.4, speed: 0.16, amp: 46, detailAmp: 18 }
    ];

    currents.forEach(drawCurrent);

    ctx.restore();
  }

  drawSoftHighlights(ctx, time) {
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.filter = 'blur(1px)';

    for (let i = 0; i < 9; i += 1) {
      const depth = i / 8;
      const baseY = this.height * (0.2 + depth * 0.62);
      const drift = time * (0.03 + depth * 0.012) + i * 0.58;
      const pointerDrift = (this.pointer.x - 0.5) * 42 * (depth - 0.5);
      const alpha = 0.055 + (1 - Math.abs(depth - 0.5)) * 0.055;

      ctx.beginPath();
      for (let step = 0; step <= 14; step += 1) {
        const x = -this.width * 0.08 + (this.width * 1.16 * step) / 14;
        const y = baseY +
          Math.sin(step * 0.66 + drift) * (12 + depth * 22) +
          Math.sin(step * 1.64 - drift * 0.8) * 6 +
          pointerDrift;

        if (step === 0)
          ctx.moveTo(x, y);
        else
          ctx.lineTo(x, y);
      }

      ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.lineWidth = 2 + depth * 3;
      ctx.stroke();
    }

    ctx.restore();
  }

  drawSignalContours(ctx, time) {
    const contours = [
      { y: 0.22, amp: 30, width: 1.5, alpha: 0.11, color: '58, 150, 188', dash: [22, 34], phase: 0.2, speed: 0.055 },
      { y: 0.39, amp: 38, width: 1.7, alpha: 0.09, color: '72, 178, 153', dash: [12, 28], phase: 1.7, speed: -0.045 },
      { y: 0.61, amp: 45, width: 1.4, alpha: 0.1, color: '58, 150, 188', dash: [18, 24], phase: 3.1, speed: 0.038 },
      { y: 0.76, amp: 34, width: 1.2, alpha: 0.08, color: '255, 255, 255', dash: [10, 22], phase: 4.6, speed: -0.033 }
    ];

    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    contours.forEach((contour) => {
      const stroke = ctx.createLinearGradient(0, 0, this.width, 0);
      stroke.addColorStop(0, `rgba(${contour.color}, 0)`);
      stroke.addColorStop(0.18, `rgba(${contour.color}, ${contour.alpha})`);
      stroke.addColorStop(0.5, `rgba(${contour.color}, ${contour.alpha * 1.22})`);
      stroke.addColorStop(0.82, `rgba(${contour.color}, ${contour.alpha})`);
      stroke.addColorStop(1, `rgba(${contour.color}, 0)`);

      ctx.beginPath();
      ctx.setLineDash(contour.dash);
      ctx.lineDashOffset = -time * 18 * (contour.speed > 0 ? 1 : -1);

      for (let step = 0; step <= 24; step += 1) {
        const flowWeight = contour.speed > 0 ? 0.22 : -0.18;
        const x = -this.width * 0.08 + (this.width * 1.16 * step) / 24 + this.flow.x * flowWeight;
        const y = this.height * contour.y +
          Math.sin(step * 0.64 + time * contour.speed + contour.phase + this.flow.phase * 0.7) * contour.amp +
          Math.sin(step * 1.42 - time * contour.speed * 1.6) * contour.amp * 0.3 +
          (this.pointer.x - 0.5) * 34 +
          this.flow.y * flowWeight;

        if (step === 0)
          ctx.moveTo(x, y);
        else
          ctx.lineTo(x, y);
      }

      ctx.strokeStyle = stroke;
      ctx.lineWidth = contour.width;
      ctx.stroke();
    });

    ctx.restore();
  }

  drawCloudNodes(ctx, time) {
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineWidth = 1;

    const nodes = [
      { x: 0.16, y: 0.28, phase: 0.2 },
      { x: 0.31, y: 0.34, phase: 0.9 },
      { x: 0.48, y: 0.27, phase: 1.6 },
      { x: 0.66, y: 0.43, phase: 2.4 },
      { x: 0.82, y: 0.36, phase: 3.2 },
      { x: 0.21, y: 0.66, phase: 3.9 },
      { x: 0.43, y: 0.72, phase: 4.5 },
      { x: 0.69, y: 0.68, phase: 5.1 }
    ];

    const points = nodes.map((node) => ({
      x: this.width * node.x + Math.sin(time * 0.11 + node.phase) * 28 + (this.pointer.x - 0.5) * 24 + this.flow.x * 0.18,
      y: this.height * node.y + Math.cos(time * 0.09 + node.phase) * 18 + (this.pointer.y - 0.5) * 18 + this.flow.y * 0.16
    }));

    ctx.beginPath();
    points.forEach((point, index) => {
      if (index === 0)
        ctx.moveTo(point.x, point.y);
      else
        ctx.lineTo(point.x, point.y);
    });

    ctx.strokeStyle = 'rgba(62, 151, 184, 0.035)';
    ctx.stroke();

    points.forEach((point, index) => {
      const pulse = 0.55 + Math.sin(time * 0.8 + index) * 0.2;
      const radius = 2.4 + pulse;
      const glow = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius * 8);

      glow.addColorStop(0, `rgba(255, 255, 255, ${0.28 + pulse * 0.1})`);
      glow.addColorStop(0.32, 'rgba(58, 150, 188, 0.09)');
      glow.addColorStop(1, 'rgba(58, 150, 188, 0)');

      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius * 8, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'rgba(50, 133, 169, 0.18)';
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius * 0.72, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
  }

  drawVignette(ctx) {
    const gradient = ctx.createRadialGradient(
      this.width * 0.5,
      this.height * 0.44,
      Math.min(this.width, this.height) * 0.18,
      this.width * 0.5,
      this.height * 0.5,
      Math.max(this.width, this.height) * 0.72
    );

    gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
    gradient.addColorStop(0.58, 'rgba(255, 255, 255, 0)');
    gradient.addColorStop(1, 'rgba(72, 124, 153, 0.12)');

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);
  }

  render() {
    return <canvas className="auth-cloud-background" aria-hidden="true" ref={(canvas) => { this.canvasRef = canvas; }} />;
  }
}

export default AuthCloudBackground;
